import { useState } from 'react';
import { Link, NavLink, useLocation, useNavigate } from 'react-router';
import { ActionMenu, Avatar, CountBadge, Dialog, Icon, IconButton, cx, useAuth, useIsCompact, useTheme, type NavItem } from '@saas/ui';
import type { NavModel } from './nav.js';
import { SidebarNav } from './Sidebar.js';
import { NavigationGroupDropdown, TopNavigation } from './TopNavigation.js';
import { useNavigationLayout } from './navigation-layout.js';

const HEADER_WORK_MODULES = ['chat', 'conversation', 'calendar', 'storage', 'meeting-ingest'] as const;
const HEADER_WORK_MODULE_SET = new Set<string>(HEADER_WORK_MODULES);
const ICON_ONLY_HEADER_MODULES = new Set<string>(['chat', 'storage']);

/**
 * C-18 ヘッダー（高さ 56 以上）。左: メニュー（1023px 以下）+ 会社名、右: テーマ切替 / 個人メニュー / ユーザー。
 * 右のアイコンだけのボタンは 2 つまで（05 §4.4-2）。
 */
export function Header({ model }: { model: NavModel }) {
  const { layout } = useNavigationLayout();
  const isCompact = useIsCompact();
  const location = useLocation();
  const { me, logout } = useAuth();
  const { resolved, toggle } = useTheme();
  const navigate = useNavigate();
  const [menuOpen, setMenuOpen] = useState(false);
  const headerItems: NavItem[] = model.primary.filter((item) => item.to === '/' || item.to === '/notifications');
  const headerWorkGroups = HEADER_WORK_MODULES.flatMap((module) => model.work.filter((group) => group.module === module));
  const topWorkGroups = model.work.filter((group) => !HEADER_WORK_MODULE_SET.has(group.module));

  return (
    <header className="sticky top-0 z-40 border-b border-line bg-card">
      <div className="flex min-h-[var(--header-height)] items-center gap-2 px-3 sm:px-5">
        <IconButton icon="menu" label="メニューを開く" className="lg:hidden" onClick={() => setMenuOpen(true)} />
        <Link to="/" className="flex min-w-0 min-tap items-center gap-2 rounded-input px-2 hover:bg-sunken">
          <span className="truncate font-serif text-title">{me?.tenant.name ?? '業務 SaaS'}</span>
        </Link>
        <div className="flex-1" />
        <IconButton icon={resolved === 'dark' ? 'light_mode' : 'dark_mode'} label={resolved === 'dark' ? 'ライトモードに切り替え' : 'ダークモードに切り替え'} onClick={toggle} />
        <nav aria-label="ヘッダーメニュー" className="hidden items-center gap-0.5 xl:gap-1 lg:flex">
          <ul className="flex items-center gap-0.5">
            {headerItems.map((item) => {
              const count = item.badge ? model.counts[item.badge] : 0;
              const iconOnly = item.to === '/notifications';
              return (
                <li key={item.to}>
                  <NavLink
                    to={item.to}
                    end={item.end}
                    aria-label={iconOnly ? (count ? `${item.label} ${count} 件` : item.label) : undefined}
                    title={iconOnly ? item.label : undefined}
                    className={({ isActive }) => cx(
                      'relative flex min-tap items-center gap-1.5 whitespace-nowrap rounded-input px-2 text-callout transition-colors',
                      isActive ? 'bg-brand-subtle font-semibold text-brand' : 'text-fg-secondary hover:bg-sunken hover:text-fg',
                    )}
                  >
                    <Icon name={item.icon} size={20} />
                    {iconOnly ? null : <span>{item.label}</span>}
                    {count ? <CountBadge count={count} label={item.label} /> : null}
                  </NavLink>
                </li>
              );
            })}
            {headerWorkGroups.map((group) => (
              group.module === 'chat' ? (
                <li key={group.module}>
                  <a
                    href="/chat"
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label="チャットを新しいタブで開く"
                    title="チャットを新しいタブで開く"
                    className="relative flex min-tap items-center justify-center rounded-input px-2 text-fg-secondary transition-colors hover:bg-sunken hover:text-fg"
                  >
                    <Icon name="chat_bubble" size={20} />
                    {model.counts['chat.unread'] ? <CountBadge count={model.counts['chat.unread']} label="チャット" className="absolute -right-1 -top-0.5" /> : null}
                  </a>
                </li>
              ) : (
                <NavigationGroupDropdown
                  key={group.module}
                  group={group}
                  counts={model.counts}
                  iconOnly={ICON_ONLY_HEADER_MODULES.has(group.module)}
                />
              )
            ))}
          </ul>
        </nav>
        <ActionMenu
          label={
            <span className="flex items-center gap-2">
              <Avatar name={me?.user.displayName} size={24} />
              <span className="hidden max-w-40 truncate sm:inline">{me?.user.displayName}</span>
            </span>
          }
          items={[
            { label: 'プロフィール・パスワード', icon: 'person', onSelect: () => navigate('/settings/profile') },
            ...(model.admin.length > 0 ? [{ label: '管理', icon: 'shield' as const, onSelect: () => navigate('/admin') }] : []),
            { label: 'セキュリティ', icon: 'shield', onSelect: () => navigate('/settings/security') },
            { label: '設定', icon: 'settings', onSelect: () => navigate('/settings') },
            { label: '通知設定', icon: 'notifications', onSelect: () => navigate('/settings/notifications') },
            { label: 'ログアウト', icon: 'logout', onSelect: () => void logout() },
          ]}
        />
      </div>
      {layout === 'top' && !isCompact ? <TopNavigation key={location.key} model={model} groups={topWorkGroups} /> : null}
      <Dialog open={menuOpen} onClose={() => setMenuOpen(false)} title="メニュー" size="md">
        <SidebarNav model={model} onNavigate={() => setMenuOpen(false)} />
      </Dialog>
    </header>
  );
}
